import { useReconciliaciones } from "@/api/queries"
import type { Reconciliacion } from "@/api/types"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearFecha } from "@/shared/format"

/**
 * Cómo se pinta cada estado.
 *
 * La decisión de diseño que importa: **solo `cuadra` se ve como "bien"**. Los cuatro
 * estados de "no se pudo comprobar" no son verdes ni neutros — se ven como avisos,
 * porque un despliegue en el que la reconciliación lleva un mes sin poder ejecutarse
 * es indistinguible de uno sano si la consola los pinta igual, y ese es exactamente el
 * modo de fallo de una red de seguridad.
 */
const ESTADOS: Record<
  string,
  { etiqueta: string; variante: "default" | "secondary" | "destructive" | "outline" }
> = {
  cuadra: { etiqueta: "cuadra", variante: "secondary" },
  desviacion: { etiqueta: "desviación", variante: "destructive" },
  sin_historial: { etiqueta: "primera foto", variante: "outline" },
  sin_lector: { etiqueta: "no comprobable", variante: "outline" },
  sin_credencial: { etiqueta: "falta credencial", variante: "outline" },
  no_disponible: { etiqueta: "no disponible", variante: "outline" },
  recarga_en_medio: { etiqueta: "recarga en medio", variante: "outline" },
  moneda_distinta: { etiqueta: "otra moneda", variante: "outline" },
}

function porcentaje(bps: number | null): string {
  if (bps === null) return "—"
  return `${(bps / 100).toFixed(2)} %`
}

function Fila({ r }: { r: Reconciliacion }) {
  const estado = ESTADOS[r.status] ?? { etiqueta: r.status, variante: "outline" as const }
  return (
    <TableRow className={r.status === "desviacion" ? "bg-destructive/5" : undefined}>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        {formatearFecha(r.created_at)}
      </TableCell>
      <TableCell className="font-medium">{r.provider_id}</TableCell>
      <TableCell>
        <Badge variant={estado.variante}>{estado.etiqueta}</Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">{r.metered}</TableCell>
      <TableCell className="text-right tabular-nums">
        {/* Un guion y no un cero: cero significaría que el proveedor no descontó nada,
            que es una afirmación muy distinta de "no se pudo saber". */}
        {r.provider ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-right tabular-nums">{porcentaje(r.divergence_bps)}</TableCell>
      <TableCell className="max-w-md text-sm text-muted-foreground">{r.note}</TableCell>
    </TableRow>
  )
}

export function ReconciliationsPage() {
  const reconciliaciones = useReconciliaciones()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Cuadre con el proveedor</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Lo que medimos <strong>atribuye</strong> el coste entre aplicaciones; lo que el
          proveedor descuenta es la <strong>verdad</strong>. Comparar las dos es lo único que
          detecta una tarifa desactualizada antes de que llegue la factura.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {reconciliaciones.isPending ? (
            <Skeleton className="h-40" />
          ) : reconciliaciones.isError ? (
            <p className="text-sm text-destructive">No se pudo cargar el cuadre.</p>
          ) : reconciliaciones.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Todavía no se ha ejecutado ninguna. Se lanza desde el servidor, programada — no
              hay botón aquí a propósito: sale a la API del proveedor con la credencial del
              cliente.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cuándo</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Medido</TableHead>
                    <TableHead className="text-right">Proveedor</TableHead>
                    <TableHead className="text-right">Desviación</TableHead>
                    <TableHead>Nota</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reconciliaciones.data.map((r) => (
                    <Fila key={r.id} r={r} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Un estado que no sea <strong>cuadra</strong> ni <strong>desviación</strong> significa
        que nadie ha comprobado nada en esa ventana. Si se repite día tras día, la red de
        seguridad no está funcionando — y eso no se nota, hay que venir a mirarlo.
      </p>
    </div>
  )
}
